import { UNLEASH_CLIENT_KEY, UNLEASH_URL } from "../../../../spond-web-common-js/src/js/constants";

export const ONE_WEB_SESSION_COOKIE_FLAG = "OneWebBffSessionCookie";

export const ONE_WEB_MINT_URL = "/v2/client/demo-spa/api/auth/session";

const UNLEASH_TIMEOUT_MS = window.location.hostname.endsWith(".cn") ? 10_000 : 2_000;

type FetchFn = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

export interface OneWebSessionDeps {
    fetchFn?: FetchFn;
    search?: string;
}

// Same `?feat_<flag>=true/false` override convention as the shared unleashClient.
function deeplinkOverride(search: string): boolean | null {
    const entries = search.slice(1).split("&");
    for (const entry of entries) {
        const [key, value] = entry.split("=");
        if (key === `feat_${ONE_WEB_SESSION_COOKIE_FLAG}`) {
            return value !== "false";
        }
    }
    return null;
}

async function fetchWithTimeout(fetchFn: FetchFn, url: string, init: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), UNLEASH_TIMEOUT_MS);
    try {
        return await fetchFn(url, { ...init, signal: controller.signal });
    } finally {
        clearTimeout(timeout);
    }
}

/**
 * Whether the one-web BFF session-cookie exchange (MAC-682) is enabled.
 * Returns false instead of rejecting, so a flag-evaluation failure can never
 * affect the login flow.
 */
export async function isOneWebSessionCookieEnabled(deps: OneWebSessionDeps = {}): Promise<boolean> {
    const search = deps.search != null ? deps.search : window.location.search;
    const override = deeplinkOverride(search);
    if (override !== null) {
        return override;
    }

    try {
        const fetchFn = deps.fetchFn || window.fetch.bind(window);
        const response = await fetchWithTimeout(
            fetchFn,
            `${UNLEASH_URL}/proxy?appName=groups-web&environment=default`,
            {
                headers: { Authorization: UNLEASH_CLIENT_KEY },
            },
        );
        if (!response.ok) {
            return false;
        }
        const data = await response.json();
        const toggle = (data?.toggles || []).find(
            (it: { name: string; enabled: boolean }) => it.name === ONE_WEB_SESSION_COOKIE_FLAG,
        );
        return toggle?.enabled || false;
    } catch (error) {
        return false;
    }
}

/**
 * POST the login's AccessRefreshTokenDto to the one-web BFF, which answers
 * with the HttpOnly session cookie. Resolves once the cookie is committed.
 * Never rejects: a failed mint must not block the login — the one-web gate
 * just sends the user back here.
 */
export async function mintOneWebSessionCookie(tokenPayload: unknown, deps: OneWebSessionDeps = {}): Promise<void> {
    try {
        const fetchFn = deps.fetchFn || window.fetch.bind(window);
        await fetchFn(ONE_WEB_MINT_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(tokenPayload),
        });
    } catch (error) {
        // Swallowed by design — see above.
    }
}
